import type {
  SearchProgramUsersRequest,
  SearchProgramUsersResponse,
} from '@compass/client/types/program_user';
import { useQuery } from '@tanstack/react-query';

import { programUserService } from '@/services/api';

export const queryKeys = {
  programUsers: {
    all: ['compass', 'programUsers'] as const,
    searchAll: () => [...queryKeys.programUsers.all, 'search'] as const,
    search: (
      organizationId: string,
      programId: string,
      params?: Partial<SearchProgramUsersRequest>,
    ) => [...queryKeys.programUsers.all, 'search', organizationId, programId, params] as const,
  },
};

interface ProgramUsersResponse extends SearchProgramUsersResponse {
  has_next_page?: boolean;
}

/**
 * Hook to search for users within a program with pagination support
 * Returns has_next_page by fetching limit + 1 items
 */
export const useProgramUsers = (
  organizationId?: string,
  programId?: string,
  params?: Omit<SearchProgramUsersRequest, 'organization_id' | 'program_id'>,
) => {
  return useQuery({
    queryKey: queryKeys.programUsers.search(organizationId || '', programId || '', params),
    queryFn: async () => {
      if (!organizationId || !programId) {
        throw new Error('Organization ID and Program ID are required');
      }

      const limit = params?.limit || 50;
      // Request one extra item to determine if there's a next page
      const response = await programUserService.searchProgramUsers({
        organization_id: organizationId,
        program_id: programId,
        ...params,
        limit: limit + 1,
      });

      // Check if we got more items than requested (indicating a next page exists)
      const hasNextPage = (response.users?.length || 0) > limit;

      // Return only the requested number of items
      const result: ProgramUsersResponse = {
        ...response,
        users: response.users?.slice(0, limit),
        has_next_page: hasNextPage,
      };

      return result;
    },
    enabled: !!organizationId && !!programId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};
